import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SortingState } from '@tanstack/react-table';
import { AlertTriangleIcon, BlocksIcon, LightbulbIcon, MessagesSquareIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams, useLocation } from 'react-router';
import { useTitle } from 'react-use';

import {
  FilterDefinition,
  SavedFilterItem,
} from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import { useColumnVisibility } from '@/components/ui/table/useColumnVisibility';
import { useServerPagination } from '@/components/ui/table/useServerPagination';
import { useModalTryIt, useSessionReplayHref } from '@/hooks/useModalTryIt';
import { getFilterValues, useTableFilters } from '@/hooks/useTableFilters';
import { selectAccountSlug } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { CampaignSubscriber } from '@/shared/types/campaigns';
import { EventName, PageName } from '@/shared/types/segment';

import { useCampaignLogsFilterBadges } from './useCampaignLogsFilterBadges';
import { useCampaigns } from './useCampaigns';
import { useCampaignSenders } from './useCampaignSenders';
import { CampaignSubscriberParams, useCampaignSubscribers } from './useCampaignSubscribers';
import { columns } from '../components/Tables/CampaignLogsTable/columns';
import { CHANNEL_OPTIONS, SORT_FIELD_MAP } from '../components/Tables/CampaignLogsTable/constants';
import { getReplayability, isUnifiedCampaignRoute } from '../utils';

interface UseCampaignLogsPageOptions {
  logsPathSegment: string;
}

interface RowInteraction {
  onRowClick?: (row: CampaignSubscriber) => void;
  getRowClickable?: (row: CampaignSubscriber) => boolean;
  getRowHref?: (row: CampaignSubscriber) => string | null;
}

export const useCampaignLogsPage = ({ logsPathSegment }: UseCampaignLogsPageOptions) => {
  const { t } = useTranslation();
  const { campaignId } = useParams<{ campaignId: string }>();
  const { pathname } = useLocation();
  const slug = useSelector(selectAccountSlug);
  const isUnifiedRoute = isUnifiedCampaignRoute(pathname);
  const [urlParams] = useSearchParams();
  const sessionIdFromUrl = urlParams.get('session_id');
  const { handleAccountReplayClick } = useModalTryIt(sessionIdFromUrl);

  // State
  const [lastSeen, setLastSeen] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>(() => urlParams.get('q') ?? '');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>(
    () => urlParams.get('q') ?? ''
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const [openPillId, setOpenPillId] = useState<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const customizeButtonRef = useRef<HTMLButtonElement>(null);
  const [isCustomizeColumnsOpen, setIsCustomizeColumnsOpen] = useState(false);

  // Data fetching
  const { campaign_senders } = useCampaignSenders(campaignId);
  const { campaign, getStatus: campaignStatus } = useCampaigns(campaignId);

  useTitle(t('pages.campaigns_logs', { 0: campaign?.name ?? '-' }));

  useSegments(PageName.CAMPAIGN_LOGS);

  // --- Filters ---

  const {
    filters,
    activeFilterCount,
    setFilter: setFilterRaw,
    removeFilter,
    resetFilters,
    savedViews,
    activeView,
    hasUnsavedChanges,
    applyView,
    clearActiveView,
    saveView,
    renameView,
    updateView,
    deleteView,
  } = useTableFilters({ storageKey: 'campaignLogsFilters', preferencesKey: 'campaign_logs' });

  const setFilter = useCallback(
    (key: string, values: string[], exclude?: boolean) => {
      trackEvent(EventName.CampaignLogsFilter, {
        campaign_id: campaignId,
        filter_type: key,
        filter_value: values.join(','),
      });
      setFilterRaw(key, values, exclude);
    },
    [campaignId, setFilterRaw]
  );

  const filterDefinitions = useMemo(
    (): FilterDefinition[] => [
      {
        id: 'error_type',
        label: t('campaigns.logs_filters.error_type'),
        icon: AlertTriangleIcon,
        type: 'text',
        options: [],
        placeholder: t('campaigns.logs_filters.error_type_placeholder'),
      },
      {
        id: 'channels',
        label: t('common.channels'),
        icon: BlocksIcon,
        type: 'checkbox',
        options: CHANNEL_OPTIONS,
      },
      {
        id: 'status',
        label: t('common.status'),
        icon: LightbulbIcon,
        type: 'radio',
        options: [
          { label: t('broadcasts.recipients_status.sent'), value: 'sent' },
          { label: t('broadcasts.recipients_status.delivered'), value: 'delivered' },
          { label: t('broadcasts.recipients_status.read'), value: 'read' },
          { label: t('broadcasts.recipients_status.failed'), value: 'failed' },
        ],
      },
      {
        id: 'replied',
        label: t('broadcasts.recipients_status.replied'),
        icon: MessagesSquareIcon,
        type: 'radio',
        options: [
          { label: t('common.yes'), value: 'replied' },
          { label: t('common.no'), value: 'not_replied' },
        ],
      },
    ],
    [t]
  );

  const filterBadges = useCampaignLogsFilterBadges({
    filters,
    filterDefinitions,
    setFilter,
    removeFilter,
  });

  const savedFilterItems: SavedFilterItem[] = useMemo(
    () => savedViews.map((v) => ({ id: v.id, name: v.name })),
    [savedViews]
  );

  const activeSavedFilterView = useMemo(
    () => (activeView ? { id: activeView.id, name: activeView.name } : null),
    [activeView]
  );

  const savedFiltersConfig = useMemo(
    () => ({
      savedFilters: savedFilterItems,
      activeSavedFilter: activeSavedFilterView,
      hasUnsavedChanges,
      onSave: saveView,
      onUpdate: updateView,
      onDelete: deleteView,
      onRename: renameView,
      onSelectView: (viewId: string) => {
        const view = savedViews.find((v) => v.id === viewId);
        if (view) {
          applyView(view);
        }
      },
    }),
    [
      savedFilterItems,
      activeSavedFilterView,
      hasUnsavedChanges,
      saveView,
      updateView,
      deleteView,
      renameView,
      savedViews,
      applyView,
    ]
  );

  // --- Replay handlers (need to be in scope for column visibility memo below) ---

  const handleSessionClick = useCallback((sessionId: string) => {
    setLastSeen((prev) =>
      prev.includes(sessionId)
        ? [...prev.filter((id) => id !== sessionId), sessionId]
        : [...prev, sessionId]
    );
  }, []);

  const triggerReplay = useCallback(
    (sessionId: string) => {
      handleSessionClick(sessionId);
      handleAccountReplayClick(sessionId);
    },
    [handleSessionClick, handleAccountReplayClick]
  );

  // --- Columns (without onStopClick — only used for visibility detection) ---

  const tableColumns = useMemo(
    () =>
      columns({
        lastSeen,
        onReplay: triggerReplay,
        campaign_senders,
        campaignStatus: campaign?.status,
        isAgentic: !!campaign?.nba_config,
        hideReplay: !!campaign?.nba_config,
      }),
    [lastSeen, triggerReplay, campaign_senders, campaign?.status, campaign?.nba_config]
  );

  const {
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    hideableColumns,
    hasHiddenColumns,
  } = useColumnVisibility(tableColumns, 'campaignLogsColumnVisibility');

  // --- Sorting → API param ---

  const sortParam = useMemo(() => {
    if (sorting.length === 0) {
      return undefined;
    }
    const { id, desc } = sorting[0];
    const field = SORT_FIELD_MAP[id];
    if (!field) {
      return undefined;
    }
    return desc ? `-${field}` : field;
  }, [sorting]);

  // --- Convert filters to API params ---

  const subscriberParams = useMemo((): CampaignSubscriberParams => {
    const statusValues = getFilterValues(filters, 'status');
    const repliedValues = getFilterValues(filters, 'replied');
    const errorTypeValues = getFilterValues(filters, 'error_type');
    const channelValues = getFilterValues(filters, 'channels');

    const status = repliedValues[0] || statusValues[0] || undefined;

    return {
      q: debouncedSearchQuery || undefined,
      status,
      error_type: errorTypeValues[0] || undefined,
      channels: channelValues.length > 0 ? channelValues : undefined,
      sort: sortParam,
    };
  }, [filters, debouncedSearchQuery, sortParam]);

  // Refetch interval: 5s if campaign was updated within the last hour
  const refetchInterval = useMemo(() => {
    if (!campaign?.updated) {
      return false;
    }
    const campaignUpdatedAt = new Date(campaign.updated);
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
    return campaignUpdatedAt > oneHourAgo ? 5000 : false;
  }, [campaign?.updated]);

  // Data
  const {
    listStatus,
    subscribersData,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage: serverHasNextPage,
    totalRows,
  } = useCampaignSubscribers(campaignId, subscriberParams, refetchInterval);

  const { currentPageIndex, setCurrentPageIndex, goToNextPage, hasNextPage, currentPageData } =
    useServerPagination(subscribersData?.pages || [], fetchNextPage, {
      dataSelector: (page) => page?.subscribers || [],
      resetDependencies: [subscriberParams],
      externalIsFetching: isFetchingNextPage,
      serverHasNextPage,
    });

  // --- Handlers ---

  const handleVisitSession = useCallback(() => {
    if (sessionIdFromUrl) {
      handleSessionClick(sessionIdFromUrl);
      handleAccountReplayClick();
    }
  }, [sessionIdFromUrl, handleSessionClick, handleAccountReplayClick]);

  useEffect(() => {
    if (sessionIdFromUrl) {
      handleVisitSession();
    }
  }, [handleVisitSession, sessionIdFromUrl]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(value);
    }, 300);
  }, []);

  // NBA campaigns navigate via the row href; the click handler only tracks.
  const handleRowClick = useCallback(() => {
    trackEvent(EventName.CampaignLogsClickRow, { campaign_id: campaignId });
  }, [campaignId]);

  const handleReplayRowClick = useCallback(
    (row: CampaignSubscriber) => {
      const { sessionId, canReplay } = getReplayability(row);
      if (!canReplay || !sessionId) {
        return;
      }
      triggerReplay(sessionId);
    },
    [triggerReplay]
  );

  const isRowReplayable = useCallback(
    (row: CampaignSubscriber) => getReplayability(row).canReplay,
    []
  );

  // NBA campaigns link to the subscriber journey; replay campaigns deep-link the replay
  // modal via ?session_id= (auto-opened by the handleVisitSession effect above)
  const getRowHrefNba = useCallback(
    (row: CampaignSubscriber) => {
      const logsPath = isUnifiedRoute ? 'logs' : logsPathSegment;
      return `/${slug}/campaigns/${campaignId}/${logsPath}/${row.subscriber_id}`;
    },
    [slug, campaignId, isUnifiedRoute, logsPathSegment]
  );

  const getSessionReplayHref = useSessionReplayHref();
  const getRowHrefReplay = useCallback(
    (row: CampaignSubscriber) => {
      const { sessionId, canReplay } = getReplayability(row);
      if (!canReplay || !sessionId) {
        return null;
      }
      return getSessionReplayHref(sessionId);
    },
    [getSessionReplayHref]
  );

  const hasNbaConfig = !!campaign?.nba_config;

  // Rows stay inert only while the campaign is in flight — the NBA/replay mode is unknown until
  // it settles. A campaign that never arrives (404, ephemeral id) falls back to replay.
  const rowInteraction = useMemo((): RowInteraction => {
    if (campaignStatus === 'pending') {
      return {};
    }
    if (hasNbaConfig) {
      return { onRowClick: handleRowClick, getRowHref: getRowHrefNba };
    }
    return {
      onRowClick: handleReplayRowClick,
      getRowClickable: isRowReplayable,
      getRowHref: getRowHrefReplay,
    };
  }, [
    campaignStatus,
    hasNbaConfig,
    handleRowClick,
    getRowHrefNba,
    handleReplayRowClick,
    isRowReplayable,
    getRowHrefReplay,
  ]);

  return {
    campaignId,
    campaign,
    campaign_senders,
    // Filters
    filters,
    activeFilterCount,
    setFilter,
    removeFilter,
    resetFilters,
    filterDefinitions,
    filterBadges,
    savedFilterItems,
    savedFiltersConfig,
    savedViews,
    applyView,
    clearActiveView,
    openPillId,
    setOpenPillId,
    // Search
    searchQuery,
    handleSearchChange,
    // Export
    subscriberParams,
    // Columns
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    hideableColumns,
    hasHiddenColumns,
    customizeButtonRef,
    isCustomizeColumnsOpen,
    setIsCustomizeColumnsOpen,
    // Sorting
    sorting,
    setSorting,
    // Pagination
    currentPageData,
    currentPageIndex,
    setCurrentPageIndex,
    goToNextPage,
    hasNextPage,
    totalRows,
    listStatus,
    isFetchingNextPage,
    // Handlers
    lastSeen,
    triggerReplay,
    onRowClick: rowInteraction.onRowClick,
    getRowClickable: rowInteraction.getRowClickable,
    getRowHref: rowInteraction.getRowHref,
    hasNbaConfig,
  };
};

export type UseCampaignLogsPageReturn = ReturnType<typeof useCampaignLogsPage>;
